import { useContext } from 'react';
import { AuthContext } from '../utils/authContext';
import { React, useEffect, useNavigate, dash, useLocation,useState, CssBaseline, TextField, Typography, Grid2, Box, FormControlLabel, Checkbox, rsk_logo, Link } from '../utils/globalImports';
import { CustomButton, Footer, isTokenValid, setRememberMeCookie, getRememberMeCookie, removeRememberMeCookie, useApi } from '../utils/globalImports';


interface User {
  userId: number;
  role: string;
  firstName: string;
  lastName: string;
}

const Login: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const { execute } = useApi();
  const { setIsAuthenticated, triggerValidation, setUser  } = useContext(AuthContext);

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  useEffect(() => {
    // Check if the token is valid
    if (isTokenValid()) {
      navigate(from, { replace: true }); // Redirect to dashboard if token is valid
      return; // Skip further execution of the effect
    }

    // Check if "Remember Me" cookie exists and pre-fill the username field
    const savedCredentials = getRememberMeCookie();
    if (savedCredentials) {
      setUsername(savedCredentials.username);
      setPassword(savedCredentials.password); // Add this line to prefill password
      setRememberMe(true); // Auto-check the "Remember Me" box if cookie is present
    }
  }, [navigate]); // Run only once on component mount

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('')
    try {
      if (validateFields(username, password)) {

        // Prepare login data
        const loginData = {
          email: username,
          password: password,
          firstName: '',
          lastName: '',
          roleName: ''
        };

        // Call API
        const { data, error } = await execute<User>('post', '/Auth/login', loginData);

        if (error) {
          setError('Invalid credentials');
          return;
        }

        if (data?.userId) {
          // Save token to localStorage
          localStorage.setItem("userId", data.userId.toString());
          localStorage.setItem("name", `${data.firstName} ${data.lastName}`);
          localStorage.setItem("role", data.role.toString());
          localStorage.setItem("email", username);
         setUser({
          userId: data.userId,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          email: username, // use the login email manually
        });
          // Handle "Remember Me" functionality
          if (rememberMe) {
            setRememberMeCookie(username, password);
          } else {
            removeRememberMeCookie();
          }

          setIsAuthenticated(true);
          await triggerValidation(); // will only call after login, not before
          navigate(from, { replace: true });
        }
        else {
          setError("Failed to retrieve token. Please try again.");
        }
      }
    }
    catch (err: any) {
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const validateFields = (username: string, password: string) => {
    let isValid = true;
    if (!username) {
      setUsernameError('Please enter a valid username');
      isValid = false;
    } else {
      setUsernameError('');
    }

    if (!password) {
      setPasswordError('Please enter a valid password');
      isValid = false;
    } else {
      setPasswordError('');
    }

    return isValid;
  };

  return (
    <>
      <CssBaseline />
      <Grid2 container className="login-container" spacing={2}>
        {/* Left Section */}
        <Grid2 className="left-section">
          <Box className="header">
            <Typography component="h1" variant="h5" className="title">
               Login -  Production Environment
            </Typography>
            <img src={dash} alt="Dash" className="dash" />
          </Box>
          <Typography className="subheader">
            Login into your account
          </Typography>

          {/* LOGIN form Section */}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
          >
            <Typography className="input-label">
              Username
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              id="userName"
              name="userName"
              placeholder="Username"
              autoComplete="username"
              autoFocus
              value={username}
              error={!!usernameError}
              className="text-field"
              onChange={handleUsernameChange}
            />
            {usernameError && (
              <Typography className="validation-message" role="alert">
                {usernameError}
              </Typography>
            )}


            <Typography className="input-label">
              Password
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              type="password"
              id="password"
              placeholder="Password"
              autoComplete="current-password"
              className="text-field"
              error={!!passwordError}
              value={password}
              onChange={handlePasswordChange}
            />
            {passwordError && (
              <Typography className="validation-message" role="alert">
                {passwordError}
              </Typography>
            )}
            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}


            <div className="remember-forgot">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label="Remember Me"
              />
              <Link to="/forgot-password" className="forgot-password">
                Forgot password?
              </Link>
            </div>


            <CustomButton
              capsule
              className='login-button'
            >
              LOGIN
            </CustomButton>
          </Box>
        </Grid2>
        {/* Right Section */}
        <Grid2 className="right-section">
          <img
            src={rsk_logo}
            alt="Logo"
            className="logo"
          />
          <Typography className="title">
            Start managing your finance faster and better
          </Typography>
        </Grid2>
      </Grid2>
      <Footer />
    </>
  );
};

export default Login;
